import { useFormContext } from '@calendly/form';
import { I18n } from '@calendly/i18n';
import { Button } from '@calendly/ui/components/button';
import { IconChevronLeft } from '@calendly/ui/components/glyphs/IconChevronLeft';
import { IconDotsCircleHorizontal } from '@calendly/ui/components/glyphs/IconDotsCircleHorizontal';
import { IconEye } from '@calendly/ui/components/glyphs/IconEye';
import { IconEyeOff } from '@calendly/ui/components/glyphs/IconEyeOff';
import { UiIcon } from '@calendly/ui/components/ui-icon';
import { useDeviceType } from '@calendly/ui/hooks/use-device-type';
import classNames from 'classnames';
import { forwardRef, useCallback } from 'react';
import { useTrack } from 'hooks/analytics_events';
import { TRACKING_EVENT_NAMES } from 'hooks/analytics_events/constants';
import { useShellSidePanelCompact } from 'shared/shell_layout';
import { useEventTypeContext } from '../../../event_type_provider';
import { usePreviewSidePanel } from '../../preview_side_panel/context';
import { useSelectedSections } from '../components/selected_sections';
import { useFormHandleSubmit } from '../hooks';
const footerContainerIsManagedCss = "fxq4nw";
const footerContainerIsPhoneCss = "fo8ef7u";
const footerContainerCss = "f1ro7zvh";

/** Preview / more options labels — collapsed to icons when the panel is narrow. */
const footerCollapsibleLabelCss = "fqohl88";
const footerSaveButtonCss = "f7dd84j";
const footerUiIconProps = {
  size: 'small',
  title: ''
};
const FooterContainer = forwardRef((props, ref) => {
  const {
    isManaged,
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  const isPhone = useDeviceType('isPhone');
  return <Component ref={ref} {...restProps} className={classNames(footerContainerCss, {
    [footerContainerIsManagedCss]: isManaged,
    [footerContainerIsPhoneCss]: isPhone
  }, props.className)} />;
});
const buttonsContainerCss = "b11z32gm";
const ButtonsContainer = forwardRef((props, ref) => {
  const {
    as: AsComponent,
    ...restProps
  } = props;
  const Component = AsComponent || 'div';
  return <Component ref={ref} {...restProps} className={classNames(buttonsContainerCss, props.className)} />;
});
export const Footer = ({
  selectedSectionRef
}) => {
  const track = useTrack();
  const isPhone = useDeviceType('isPhone');
  const isShellSidePanelCompact = useShellSidePanelCompact();
  const handleSubmit = useFormHandleSubmit();
  const {
    isSubmitting
  } = useFormContext();
  const {
    eventType,
    isCreation
  } = useEventTypeContext();
  const {
    isPreviewSidePanelOpen,
    togglePreviewSidePanel
  } = usePreviewSidePanel();
  const {
    selectedSections,
    selectSection,
    closeCurrentSection
  } = useSelectedSections();
  const {
    isMETParent
  } = eventType;
  const useCompactFooterButtons = isShellSidePanelCompact && !isPhone;
  const previewLabel = isPreviewSidePanelOpen ? I18n.t('publisher.event_types.preview.hide_preview') : I18n.t('publisher.event_types.preview.show_preview');
  const moreOptionsLabel = selectedSections.length ? I18n.t('publisher.action.back') : I18n.t('publisher.booking_modal.more_options');
  const saveLabel = isCreation ? I18n.t('publisher.action.create') : I18n.t('publisher.event_type_editor.footer.save_changes');
  const handleSecondaryClick = useCallback(() => {
    if (isCreation && selectedSectionRef) {
      selectedSectionRef.current = selectedSections.length ? null : 'more_options';
    }
    return handleSubmit(isCreation ? null : () => {
      if (selectedSections.length) {
        closeCurrentSection();
      } else {
        selectSection({
          key: 'more_options'
        });
        track({
          name: TRACKING_EVENT_NAMES.EVENT_TYPE_EDITOR_INTERACTION,
          properties: {
            event_type_uuid: eventType.uuid,
            section: 'initial_editor_page',
            action: 'click_more_options'
          }
        });
      }
    });
  }, [closeCurrentSection, eventType.uuid, handleSubmit, isCreation, selectSection, selectedSectionRef, selectedSections.length, track]);
  const handlePrimaryClick = useCallback(() => {
    if (isCreation && selectedSectionRef) {
      selectedSectionRef.current = null;
    }
    handleSubmit();
  }, [isCreation, selectedSectionRef, handleSubmit]);
  const secondaryButton = <Button decoration="secondary-outline" onClick={handleSecondaryClick} disabled={isSubmitting} size={useCompactFooterButtons ? 'small' : undefined} title={useCompactFooterButtons ? moreOptionsLabel : undefined}>
      {(selectedSections.length || useCompactFooterButtons) && <UiIcon icon={selectedSections.length ? IconChevronLeft : IconDotsCircleHorizontal} {...footerUiIconProps} />}
      <span className={useCompactFooterButtons ? footerCollapsibleLabelCss : undefined}>
        {moreOptionsLabel}
      </span>
    </Button>;
  return <FooterContainer isManaged={isMETParent} data-testid="event-type-editor-footer">
      <ButtonsContainer>
        {!isCreation && !isMETParent && <Button decoration={isPhone ? 'secondary-outline' : 'ghost'} onClick={togglePreviewSidePanel} size={useCompactFooterButtons ? 'small' : undefined} title={useCompactFooterButtons ? previewLabel : undefined}>
            <UiIcon icon={isPreviewSidePanelOpen ? IconEyeOff : IconEye} {...footerUiIconProps} />
            <span className={footerCollapsibleLabelCss}>{previewLabel}</span>
          </Button>}
        {isPhone && secondaryButton}
      </ButtonsContainer>
      <ButtonsContainer>
        {!isPhone && secondaryButton}
        <Button className={footerSaveButtonCss} onClick={handlePrimaryClick} loading={isSubmitting} size={useCompactFooterButtons ? 'small' : undefined}>
          <span>{saveLabel}</span>
        </Button>
      </ButtonsContainer>
    </FooterContainer>;
};

require("./footer.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./footer.js");